import React, { FC, useEffect, useRef } from 'react';

import LoadingAnimation from 'components/loading-animation';
import Modal from 'components/modal';
import { LOADING_ANIMATION_AS_REFRESH_CONTENT } from 'components/offers/ui-refresh/constants';
import { MobileBackButton } from 'components/offers/ui-refresh/mobile-back-button';
import { useEnablePullToReloadOnScroll } from 'components/pull-to-reload';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';

import { LoyaltyOffersCooldown } from '../loyalty-offers/loyalty-offers-cooldown';
import { LoyaltyPullToReload } from '../loyalty-pull-to-reload';

import { IncentiveDetails } from './incentive-details';
import {
  IncentiveList,
  IncentivesGrid,
  LoadingAnimationContainer,
} from './loyalty-incentives.styled';

const LoyaltyIncentivesGrid: FC<any> = ({
  selectedIncentive,
  engineIncentivesMap,
  goBack,
  children,
  refetch,
  setScrollContainerRef,
  offersInCooldown = false,
  isWidget,
  isCarousel,
  setSelectedRewardIdFromWidget,
  challengeIncentiveSelected,
}) => {
  const isIncentiveSelected = Boolean(selectedIncentive);

  const { getScrollPositionForReload, pullToReloadEnabled, setScrollRef } =
    useEnablePullToReloadOnScroll();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const incentiveDetailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isIncentiveSelected) {
      // We need to scroll the incentive details container back into view
      // because sometimes the incentive list is long and if you click on
      // an offer from the lower end of the page the details do not show up
      incentiveDetailsRef.current?.scrollIntoView();
    }
  }, [isIncentiveSelected]);

  return (
    <>
      {isMobile && isIncentiveSelected && <MobileBackButton to={goBack} />}
      <IncentivesGrid
        incentiveSelected={isIncentiveSelected}
        isWidget={isWidget}
        isCarousel={isCarousel}
      >
        <IncentiveList
          data-testid="incentives-list"
          onScroll={getScrollPositionForReload}
          ref={el => {
            if (setScrollContainerRef) {
              setScrollContainerRef(el);
            }
            setScrollRef(el);
          }}
        >
          {offersInCooldown && <LoyaltyOffersCooldown />}
          {/* If it's a widget, don't allow to pull on reload */}
          {isWidget ? (
            children
          ) : (
            <LoyaltyPullToReload
              onRefresh={refetch}
              enabled={pullToReloadEnabled}
              refreshContent={
                LOADING_ANIMATION_AS_REFRESH_CONTENT ? (
                  <LoadingAnimationContainer>
                    <LoadingAnimation fillColor={Styles.color.primary} />
                  </LoadingAnimationContainer>
                ) : undefined
              }
            >
              {children}
            </LoyaltyPullToReload>
          )}
        </IncentiveList>
        {selectedIncentive &&
          (isWidget ? (
            <Modal
              onDismiss={() => setSelectedRewardIdFromWidget('')}
              mParticleEventData={{
                modalAppearanceEventMessage: 'Show reward modal',
              }}
              backgroundColor={Styles.color.white}
            >
              <IncentiveDetails
                engineIncentivesMap={engineIncentivesMap}
                selectedIncentive={selectedIncentive}
              />
            </Modal>
          ) : (
            <div data-testid="incentive-details-container" ref={incentiveDetailsRef}>
              <IncentiveDetails
                engineIncentivesMap={engineIncentivesMap}
                selectedIncentive={selectedIncentive}
                challengeIncentiveSelected={challengeIncentiveSelected}
              />
            </div>
          ))}
      </IncentivesGrid>
    </>
  );
};

export default LoyaltyIncentivesGrid;
